import axios, { CancelTokenSource } from 'axios';
import { inject } from 'mobx-react';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import {
  CountryCodeType,
  getRestaurantTranslations,
  RestaurantTranslationType,
  saveRestaurantTranslations,
} from '../../../../api/translation.api';
import { IRootStore } from '../../../../stores/RootStore';
import { FlashMessageType } from '../../../../stores/UIStore';
import { stringEmptyOrWhitespaceOnly } from '../../../../utils/text.utils';
import { ComputeLanguageFunc, RestaurantStateType, SaveTranslationFunc } from '../../Translator';
import RestaurantForm from '../forms/RestaurantForm';
import TranslationDashboard from '../TranslationDashboard';

const initFormState = {
  id: -1,
  name: '',
  about: '',
  info: '',
  note: '',
  onlineMaxGuestsText: '',
  policyText: '',
};

const getStrings = (rt: RestaurantTranslationType): Omit<RestaurantTranslationType, 'id'> => ({
  name: stringEmptyOrWhitespaceOnly(rt.name) ? '' : rt.name,
  about: stringEmptyOrWhitespaceOnly(rt.about) ? null : rt.about,
  info: stringEmptyOrWhitespaceOnly(rt.info) ? null : rt.info,
  note: stringEmptyOrWhitespaceOnly(rt.note) ? null : rt.note,
  onlineMaxGuestsText: stringEmptyOrWhitespaceOnly(rt.onlineMaxGuestsText)
    ? null
    : rt.onlineMaxGuestsText,
  policyText: stringEmptyOrWhitespaceOnly(rt.policyText) ? null : rt.policyText,
});

const RestaurantTranslator = ({
  store,
  onSave,
  computeLanguages,
}: {
  store?: IRootStore;
  onSave: SaveTranslationFunc;
  computeLanguages: ComputeLanguageFunc;
}) => {
  const [restaurantTranslations, setRestaurantTranslations] = useState<RestaurantStateType>({
    en: initFormState,
    is: initFormState,
    es: initFormState,
    nb: initFormState,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentServerData, setCurrentServerData] = useState<RestaurantStateType | null>(null);

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();
    fetchRestaurantTranslations(cancelToken);

    return () => cancelToken.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRestaurantTranslations = (cancelToken: CancelTokenSource) => {
    getRestaurantTranslations(cancelToken)
      .then((res) => {
        const translations = res.data;
        Object.keys(translations).map((c) => {
          const code = c as CountryCodeType;
          if (!translations[code]) {
            // Initialise empty state
            translations[code] = { ...initFormState };
            return;
          }

          // Make sure that only id and translation strings are in the state
          translations[code] = {
            id: translations[code]!.id,
            ...getStrings(translations[code]!),
          };
        });

        setRestaurantTranslations(translations as RestaurantStateType);
        setCurrentServerData(JSON.parse(JSON.stringify(translations)));
        setLoading(false);
      })
      .catch((e) => {
        if (!axios.isCancel(e)) {
          store!.UIStore.addFlashMessage({
            type: FlashMessageType.Error,
            text: 'Could not fetch translations',
          });
        }
        setLoading(false);
        setError(true);
      });
  };

  if (loading) {
    return <div>Loading..</div>;
  }

  if (error) {
    return <div>Something went wrong</div>;
  }

  return (
    <TranslationDashboard
      title={restaurantTranslations.en?.name ?? '(N/A)'}
      languages={computeLanguages(restaurantTranslations, getStrings)}
      dashboardType="restaurant"
      saveConfig={{
        onSave: () =>
          onSave(restaurantTranslations, getStrings, saveRestaurantTranslations).then(() => {
            const cancelToken = axios.CancelToken.source();
            fetchRestaurantTranslations(cancelToken);
          }),
        unsavedChanges:
          JSON.stringify(currentServerData) !== JSON.stringify(restaurantTranslations),
      }}
    >
      <RestaurantForm translationState={[restaurantTranslations, setRestaurantTranslations]} />
    </TranslationDashboard>
  );
};

export default inject('store')(observer(RestaurantTranslator));
